import { useEffect, useCallback, useRef, useState } from 'react';
import { getSyncQueue, removeSyncAction, incrementRetry } from '../store/offlineStore';
import { apiClient } from '../api/client';

export function useOfflineSync() {
  const isSyncing = useRef(false);
  const [pendingCount, setPendingCount] = useState(0);
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  const sync = useCallback(async () => {
    setIsOnline(navigator.onLine);
    if (isSyncing.current) return;
    if (!navigator.onLine) {
      const queue = await getSyncQueue();
      setPendingCount(queue.length);
      return;
    }

    const queue = await getSyncQueue();
    setPendingCount(queue.length);
    if (queue.length === 0) return;

    isSyncing.current = true;

    for (const action of queue) {
      if (action.retries >= 5) {
        await removeSyncAction(action.id!);
        continue;
      }

      try {
        if (action.type === 'review') {
          await apiClient.post('/progress/review', action.payload);
        } else if (action.type === 'quiz') {
          await apiClient.post('/progress/quiz', action.payload);
        } else if (action.type === 'progress') {
          await apiClient.post('/progress/batch', action.payload);
        }
        await removeSyncAction(action.id!);
      } catch {
        await incrementRetry(action.id!);
      }
    }

    const remaining = await getSyncQueue();
    setPendingCount(remaining.length);
    isSyncing.current = false;
  }, []);

  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      sync();
    };
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Initial sync on mount
    sync();

    // Periodic sync every 30 seconds
    const interval = setInterval(sync, 30000);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      clearInterval(interval);
    };
  }, [sync]);

  return { sync, pendingCount, isOnline };
}
